import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { Button, globalMessageHandler } from "common";
import authService from "features/auth/authApi";
import I18n from "i18n";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function VerifyEmailChangeForm() {
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState<number | null>(null);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Extract user_id and token from URL parameters
    const urlUserId = searchParams.get("user_id");
    const urlToken = searchParams.get("token");

    if (urlUserId) {
      const parsedUserId = parseInt(urlUserId, 10);
      if (!isNaN(parsedUserId)) {
        setUserId(parsedUserId);
      }
    }
    if (urlToken) {
      setToken(urlToken);
    }

    // Auto-verify if we have both parameters
    if (urlUserId && urlToken) {
      const parsedUserId = parseInt(urlUserId, 10);
      if (!isNaN(parsedUserId)) {
        handleVerify(parsedUserId, urlToken);
      }
    }
  }, [searchParams]);

  const handleVerify = async (
    userIdToVerify: number,
    tokenToVerify: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      const {
        success,
        data,
        error: apiError,
      } = await authService.verifyEmailChange({
        user_id: userIdToVerify,
        token: tokenToVerify,
      });

      if (success) {
        setSuccess(true);
        globalMessageHandler.snack({
          message: data?.message || I18n.t("Auth.verifyEmail.success"),
          severity: "success",
        });

        // Redirect to login after a brief delay
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      } else {
        const errorMessage =
          apiError?.error?.message ||
          I18n.t("Auth.verifyEmail.errors.failedToVerify");
        setError(errorMessage);
        globalMessageHandler.snack({
          message: errorMessage,
          severity: "error",
        });
      }
    } catch (err) {
      const errorMessage = I18n.t("Auth.verifyEmail.errors.networkError");
      setError(errorMessage);
      globalMessageHandler.snack({
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (userId && token) {
      handleVerify(userId, token);
    }
  };

  if (loading) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          margin: "0 auto",
          mt: 4,
          textAlign: "center",
        }}
      >
        <VerifiedUserIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {I18n.t("Auth.verifyEmail.verifyingTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {I18n.t("Auth.verifyEmail.verifyingMessage")}
        </Typography>
      </Paper>
    );
  }

  if (success) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          margin: "0 auto",
          mt: 4,
          textAlign: "center",
        }}
      >
        <VerifiedUserIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {I18n.t("Auth.verifyEmail.successTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {I18n.t("Auth.verifyEmail.successMessage")}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {I18n.t("Auth.verifyEmail.redirectMessage")}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={() => navigate("/login")}
            variant="contained"
            fullWidth
          >
            {I18n.t("Auth.verifyEmail.goToLogin")}
          </Button>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          margin: "0 auto",
          mt: 4,
          textAlign: "center",
        }}
      >
        <VerifiedUserIcon sx={{ fontSize: 60, color: "error.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {I18n.t("Auth.verifyEmail.errorTitle")}
        </Typography>

        <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
          {error}
        </Alert>

        <Typography variant="body1" color="text.secondary" paragraph>
          {I18n.t("Auth.verifyEmail.linkExpiredMessage")}
        </Typography>

        <Box sx={{ mt: 3, display: "flex", gap: 2, flexDirection: "column" }}>
          {userId && token && (
            <Button onClick={handleRetry} variant="outlined" fullWidth>
              {I18n.t("Auth.verifyEmail.tryAgainButton")}
            </Button>
          )}
          <Button
            onClick={() => navigate("/login")}
            variant="contained"
            fullWidth
          >
            {I18n.t("Auth.verifyEmail.goToLogin")}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Missing parameters
  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 500,
        margin: "0 auto",
        mt: 4,
        textAlign: "center",
      }}
    >
      <VerifiedUserIcon sx={{ fontSize: 60, color: "warning.main", mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        {I18n.t("Auth.verifyEmail.invalidLinkTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {I18n.t("Auth.verifyEmail.invalidLinkMessage")}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {I18n.t("Auth.verifyEmail.contactSupportMessage")}
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Button
          onClick={() => navigate("/login")}
          variant="contained"
          fullWidth
        >
          {I18n.t("Auth.verifyEmail.goToLogin")}
        </Button>
      </Box>
    </Paper>
  );
}
